import React, { type ComponentProps, type PropsWithChildren } from 'react';
import { defineMessages, type MessageValue, type NoMessageValues, useIntl } from 'react-intl';
import { Platform, type ScrollViewProps, StyleSheet } from 'react-native';
import Animated, { useAnimatedRef, useScrollOffset } from 'react-native-reanimated';

import {
  type AddressType,
  BodyText,
  CustomNavigationHeader,
  theme,
  useResponsive,
} from '@sg/garnish';

import { BagViewMenuCta } from '../../Ctas/BagViewMenuCta';
import { BagChannelAndLocationInfo } from '../../Header';

// Without the empty fragment around the header, the styling breaks.
/* eslint-disable react/jsx-no-useless-fragment */

/**
 * ScrollView for the bag.
 * Contains a header with the menu and close buttons.
 * When scrolled displays order channel and location.
 */
export const BagScrollView = (props: BagScrollViewProps) => {
  const {
    children,
    orderChannel,
    locationName,
    isGroupOrder,
    groupOrderHostName,
    isGroupOrderParticipant,
    canUpdateOrderDetails,
    viewMenuCta,
    viewMenuA11y,
    isDisabled,
    automaticallyAdjustKeyboardInsets,
    onViewMenu,
    requestChannelOrLocationChange,
    requestDeliveryPreferencesChange,
    onHeaderCloseBtnPress,
  } = props;

  const { formatMessage } = useIntl();
  const { minWidth } = useResponsive();
  const animatedRef = useAnimatedRef<Animated.ScrollView>();
  const scrollOffsetSV = useScrollOffset(animatedRef);

  const shouldShowParticipantBackButton = isGroupOrderParticipant && !minWidth.isSM;
  const shouldShowViewMenuCta = !isGroupOrderParticipant;
  const title = isGroupOrder
    ? formatMessage(messages.groupOrderTitle, { host_name: groupOrderHostName })
    : formatMessage(messages.title);

  return (
    <Animated.ScrollView
      automaticallyAdjustKeyboardInsets={automaticallyAdjustKeyboardInsets}
      ref={animatedRef}
      style={styles.bagScrollView}
      contentContainerStyle={styles.bagScrollView}
      stickyHeaderIndices={[0]}
    >
      <ScrollViewChildFragment>
        <CustomNavigationHeader.Container
          palette="cream"
          safeAreaEdges={safeAreaEdges}
          buttonSize={theme.spacing['16']}
          scrollOffsetSV={scrollOffsetSV}
        >
          {shouldShowViewMenuCta ? (
            <CustomNavigationHeader.Item.Left>
              <BagViewMenuCta
                cta={viewMenuCta}
                a11yLabel={viewMenuA11y}
                isDisabled={isDisabled}
                onPress={onViewMenu}
              />
            </CustomNavigationHeader.Item.Left>
          ) : null}

          {shouldShowParticipantBackButton ? (
            <CustomNavigationHeader.Button.Left
              name="IconArrowLeft"
              accessibilityLabel={viewMenuA11y}
              disabled={isDisabled}
              onPress={onViewMenu}
            />
          ) : null}

          <CustomNavigationHeader.Content scrollOffsetSV={scrollOffsetSV}>
            <BodyText style={styles.headerText} sizeMatch={['16']}>
              {title}
            </BodyText>

            <BagChannelAndLocationInfo
              orderChannel={orderChannel}
              locationName={locationName}
              isDisabled={!canUpdateOrderDetails}
              requestChannelOrLocationChange={requestChannelOrLocationChange}
              requestDeliveryPreferencesChange={requestDeliveryPreferencesChange}
            />
          </CustomNavigationHeader.Content>

          <CustomNavigationHeader.Button.Right
            name="IconClose"
            accessibilityLabel={formatMessage(messages.closeBag)}
            onPress={onHeaderCloseBtnPress}
          />
        </CustomNavigationHeader.Container>
      </ScrollViewChildFragment>

      {children}
    </Animated.ScrollView>
  );
};

/**
 * This is needed to avoid passing in the style from the scrollview to its children without React warnings.
 */
const ScrollViewChildFragment = (props: PropsWithChildren) => {
  return <>{props.children}</>;
};

// ─── Constants ──────────────────────────────────────────────────────────────

const safeAreaEdges = Platform.select<SafeAreaEdges>({
  android: ['top'],
  default: [],
});

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly title: NoMessageValues;
  readonly groupOrderTitle: { readonly host_name: MessageValue };
  readonly closeBag: NoMessageValues;
}>({
  title: {
    defaultMessage: 'Your bag',
    description: 'Bag | Header | Title',
  },
  groupOrderTitle: {
    defaultMessage: "{host_name}'s group order",
    description: 'Bag | Header | Group order title',
  },
  closeBag: {
    defaultMessage: 'Close Bag',
    description: 'Bag | Header | Close bag button',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  headerText: {
    textAlign: 'center',
  },
  bagScrollView: {
    flexGrow: 1,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type SafeAreaEdges = ComponentProps<typeof CustomNavigationHeader.Container>['safeAreaEdges'];

type BagScrollViewProps = ScrollViewProps & {
  orderChannel: AddressType;
  locationName: string;
  isGroupOrder: boolean;
  groupOrderHostName: string;
  isGroupOrderParticipant: boolean;
  canUpdateOrderDetails: boolean;
  viewMenuCta: string;
  viewMenuA11y: string;
  isDisabled: boolean;
  automaticallyAdjustKeyboardInsets: boolean;
  onViewMenu: () => void;
  requestChannelOrLocationChange: () => void;
  requestDeliveryPreferencesChange: () => void;
  onHeaderCloseBtnPress: () => void;
};
